/**
 * World Courier booking template filtering by portal project (site).
 *
 * A template with no `wc_project` is "universal" (legacy / visible everywhere).
 * A tagged template is only shown once its project matches the selected one.
 */

export interface WcTemplateProjectRow {
  wc_project?: string | null;
}

function normProject(v: string | null | undefined): string {
  return (v ?? '').trim();
}

/** Whether a template may be used under the given selected project.
 *  Untagged templates are compatible with any project (including none). */
export function isWcTemplateCompatibleWithProject(
  template: WcTemplateProjectRow,
  selectedProject: string | null | undefined
): boolean {
  const tpl = normProject(template.wc_project);
  if (!tpl) return true;
  return tpl === normProject(selectedProject);
}

/**
 * Filter templates for the origin/destination autocomplete dropdowns.
 *
 * - `selectedProject` empty → only untagged templates (tagged ones hidden until
 *   a project is picked, so cross-site profiles don't leak before selection).
 * - `selectedProject` set → untagged templates OR templates tagged with it.
 */
export function filterWcTemplatesByProject<T extends WcTemplateProjectRow>(
  templates: T[],
  selectedProject: string | null | undefined
): T[] {
  const selected = normProject(selectedProject);
  if (!selected) {
    return templates.filter((t) => !normProject(t.wc_project));
  }
  return templates.filter((t) => {
    const tpl = normProject(t.wc_project);
    return !tpl || tpl === selected;
  });
}
